/**
 * useHistory - hook for undo/redo state management
 * Connects to local HistoryManager
 */

import { useCallback, useState, useEffect } from 'react';
import { historyManager } from '@/runtime/web';

interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
}

export function useHistory(): HistoryState {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Update state after operations
  const updateState = useCallback(() => {
    setCanUndo(historyManager.canUndo());
    setCanRedo(historyManager.canRedo());
  }, []);

  // Check state periodically (for external changes)
  useEffect(() => {
    const interval = setInterval(updateState, 500);
    return () => clearInterval(interval);
  }, [updateState]);

  const undo = useCallback(() => {
    if (historyManager.undo()) {
      updateState();
    }
  }, [updateState]);

  const redo = useCallback(() => {
    if (historyManager.redo()) {
      updateState();
    }
  }, [updateState]);

  return {
    canUndo,
    canRedo,
    undo,
    redo
  };
}
